import Tooltip from '@mui/material/Tooltip'
import type { ConfigurableProperty, DigitalTwinTranslations } from 'api/digitalTwin/digitalTwin.api'
import { getSelectedProperties } from 'helpers/appliedSettingValues.helper/propertySelection.helper'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, LoadingPlaceholderContainer } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import { OPTJOB_TYPE_NAME_TO_ID } from 'ui/uiConfig/template/aliases'
import { AppliedSettingValues } from 'views/DigitalTwinCollectedSettingsView/components/AppliedSettingValues/AppliedSettingValues'
import {
  getOptimizationTypeOptionDisplayLabel,
  type OptimizationSubtypeOption,
} from 'views/DigitalTwinCollectedSettingsView/DigitalTwinCollectedSettingsView.helper'
import { OUTCOME_OPTJOB_TYPE, toOptimizationTypeAliases } from './CollectedSettingsAppliedSettingsTable.helper'

type CollectedSettingsAppliedSettingsTableProps = {
  period: { startTime: ISODateTime; endTime: ISODateTime } | null
  optimizationOptions: OptimizationSubtypeOption[]
  selectedOptimizationId: string | null
  disabled: boolean
  allProperties: ConfigurableProperty[]
  availableFiltersSubset: string[][]
  filterTags: string[]
  isFiltered: boolean
  translations: DigitalTwinTranslations
}

function DisabledAppliedSettingsCard({ tooltip }: { tooltip: string }): ReactNode {
  const { t } = useTranslation()

  return (
    <Tooltip title={tooltip} arrow>
      <div className="relative z-0 w-full min-w-0 opacity-50 cursor-not-allowed">
        <Card title={t('Applied settings values')} titleSize="large">
          {null}
        </Card>
      </div>
    </Tooltip>
  )
}

export function CollectedSettingsAppliedSettingsTable({
  period,
  optimizationOptions,
  selectedOptimizationId,
  disabled,
  allProperties,
  availableFiltersSubset,
  filterTags,
  isFiltered,
  translations,
}: CollectedSettingsAppliedSettingsTableProps): ReactNode {
  const { t } = useTranslation()
  const { hasAccess } = useAuth()
  const hasAppliedSettingsTableAccess = hasAccess({
    module: 'followupproduction',
    submodule: 'followupproduction_setting_table',
  })
  const selectedOption = optimizationOptions.find((option) => option.id === selectedOptimizationId)
  const optimalOptJobTypeId = selectedOption ? OPTJOB_TYPE_NAME_TO_ID[selectedOption.optJobType] : undefined
  const outcomeOptJobTypeId = OPTJOB_TYPE_NAME_TO_ID[OUTCOME_OPTJOB_TYPE]

  const cardTitle = selectedOption
    ? `${t('Applied settings values')} - ${getOptimizationTypeOptionDisplayLabel(selectedOption)}`
    : t('Applied settings values')

  if (!hasAppliedSettingsTableAccess) {
    return null
  }
  if (disabled) {
    return (
      <DisabledAppliedSettingsCard
        tooltip={t('To see applied settings values, choose Optimization type in the header')}
      />
    )
  }
  if (!selectedOption || optimalOptJobTypeId === undefined || outcomeOptJobTypeId === undefined) {
    return null
  }
  if (selectedOption.isSplitIntoSubtypes) {
    return <DisabledAppliedSettingsCard tooltip={t('To see applied settings values, choose a subtype')} />
  }
  const priorityLevels = selectedOption.priorityLevels.toReversed() // Should start with the highest prio level to show the correct applied value
  const overrideAlias = toOptimizationTypeAliases(selectedOption, priorityLevels, {
    optimal: optimalOptJobTypeId,
    outcome: outcomeOptJobTypeId,
  })
  return (
    <div className="relative z-0 w-full min-w-0">
      <Card title={cardTitle} titleSize="large" collapsible defaultCollapsed rememberCollapsed>
        <InfoBanner
          text={t(
            'The values are not affected by the deviation type filter. The values here always represent all priority levels for the chosen optimization type.'
          )}
          variant="text"
        />
        {period ? (
          <AppliedSettingValues
            period={period}
            optimalSubtype={selectedOption.subtype}
            overrideAlias={overrideAlias}
            priorityLevels={priorityLevels}
            allProperties={allProperties}
            availableFiltersSubset={availableFiltersSubset}
            selectedPriorityLevels={selectedOption.priorityLevels}
            selectedProperties={getSelectedProperties(allProperties, selectedOption.priorityLevels, filterTags)}
            isFiltered={isFiltered}
            translations={translations}
          />
        ) : (
          <LoadingPlaceholderContainer height={100} />
        )}
      </Card>
    </div>
  )
}
